import { useQueryClient } from '@tanstack/react-query';
import useMutationApi from 'src/api/useMutationApi';
import { ProductV2DTO } from 'src/types/application/inventory-management/ProductV2DTO';

interface UpdateProductAttributeData {
  isRequired?: boolean;
  defaultValue?: unknown;
}

interface UpdateProductAttributeResponse {
  productAttribute: {
    id: string;
    productId: string;
    customFieldId: string;
    isRequired: boolean;
    defaultValue: unknown;
  };
}

interface ProductQueryData {
  product: ProductV2DTO;
}

interface MutationContext {
  previousData: [unknown, unknown][];
}

export function useUpdateProductAttribute(
  productId: string,
  attributeId: string,
  onSuccess?: (response: UpdateProductAttributeResponse) => void,
) {
  const queryClient = useQueryClient();

  return useMutationApi<
    UpdateProductAttributeData,
    UpdateProductAttributeResponse,
    UpdateProductAttributeResponse,
    MutationContext
  >({
    endpoint: `inventory-management/products-v2/${productId}/attributes/${attributeId}`,
    method: 'PATCH',
    options: {
      onMutate: async (variables) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            if (Array.isArray(queryKey) && typeof queryKey[0] === 'string') {
              return queryKey[0].includes(
                `inventory-management/products-v2/${productId}`,
              );
            }
            return false;
          },
        });

        // Snapshot previous value
        const previousData = queryClient.getQueriesData({
          predicate: (query) => {
            const queryKey = query.queryKey;
            if (Array.isArray(queryKey) && typeof queryKey[0] === 'string') {
              return queryKey[0].includes(
                `inventory-management/products-v2/${productId}`,
              );
            }
            return false;
          },
        });

        // Optimistically update
        queryClient.setQueriesData(
          {
            predicate: (query) => {
              const queryKey = query.queryKey;
              if (Array.isArray(queryKey) && typeof queryKey[0] === 'string') {
                return queryKey[0].includes(
                  `inventory-management/products-v2/${productId}`,
                );
              }
              return false;
            },
          },
          (old: ProductQueryData | undefined) => {
            if (!old?.product?.attributes) return old;

            return {
              ...old,
              product: {
                ...old.product,
                attributes: old.product.attributes.map((attr) =>
                  attr.id === attributeId
                    ? {
                        ...attr,
                        ...(variables.isRequired !== undefined && {
                          isRequired: variables.isRequired,
                        }),
                        ...(variables.defaultValue !== undefined && {
                          defaultValue: variables.defaultValue,
                        }),
                      }
                    : attr,
                ),
              },
            };
          },
        );

        return { previousData };
      },
      onError: (_error, _variables, context) => {
        // Rollback on error
        if (context?.previousData) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          context.previousData.forEach(([queryKey, data]: any) => {
            queryClient.setQueryData(queryKey, data);
          });
        }
      },
      onSuccess: (response) => {
        onSuccess?.(response.data);
      },
      onSettled: () => {
        // Refetch to sync with server
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            if (Array.isArray(queryKey) && typeof queryKey[0] === 'string') {
              return queryKey[0].includes(
                `inventory-management/products-v2/${productId}`,
              );
            }
            return false;
          },
        });
      },
    },
    successMessage: 'Product attribute updated successfully',
  });
}
